import {
  MetricsInterval,
  QUEUE_SERIES_COLORS,
  QUEUE_SERIES_FALLBACK_COLOR,
} from 'src/containers/metrics/constants';
import {
  IntegrationTimeseries,
  MetricsChartData,
  QueueSeriesUnit,
  QueueSetTimeseries,
  QueueTimeseries,
  QueueTimeseriesResponse,
} from 'src/containers/metrics/types';

import { serializeQueryParameters } from './helpers';
import { monitoringAPI } from './monitoring';
import { getScoringTimeseriesRange } from './scoringHealth';

const UNIT_ORDER: QueueSeriesUnit[] = ['count', 'per_sec', 'percent', 'mb'];

const UNIT_LABEL: Record<QueueSeriesUnit, string> = {
  count: 'Counts',
  per_sec: 'Per second',
  percent: 'Percent',
  mb: 'Megabytes',
};

const toChartTimestamp = (value: number) =>
  value < 1_000_000_000_000 ? value * 1000 : value;

const queueCharts = (
  integration: IntegrationTimeseries,
  setName: 'request' | 'response',
  queueSet: QueueSetTimeseries,
  timestamps: number[],
  stepMs: number,
  interval: MetricsInterval
): MetricsChartData[] => {
  const queues = [queueSet.main, queueSet.retry, queueSet.parking].filter(
    (queue): queue is QueueTimeseries => Boolean(queue)
  );

  return queues.flatMap((queue) => {
    const byUnit = new Map<string, typeof queue.series>();

    queue.series.forEach((series) => {
      const current = byUnit.get(series.unit) || [];
      current.push(series);
      byUnit.set(series.unit, current);
    });

    const units = [
      ...UNIT_ORDER.filter((unit) => byUnit.has(unit)),
      ...Array.from(byUnit.keys()).filter(
        (unit) => !UNIT_ORDER.includes(unit as QueueSeriesUnit)
      ),
    ];

    return units.map((unit) => ({
      id: `${integration.key}:${setName}:${queue.kind}:${unit}`,
      title: [
        integration.name,
        setName,
        queue.kind,
        UNIT_LABEL[unit as QueueSeriesUnit] || unit,
      ]
        .join(' · ')
        .concat(` (${integration.status})`),
      interval,
      timestamps,
      stepMs,
      series: (byUnit.get(unit) || []).map((item) => ({
        key: item.key,
        name: item.name,
        color: QUEUE_SERIES_COLORS[item.key] || QUEUE_SERIES_FALLBACK_COLOR,
        values: item.values,
        stats: {
          mean: item.stats?.mean ?? null,
          last: item.stats?.last ?? null,
          max: item.stats?.max ?? null,
          total: item.stats?.total ?? null,
        },
      })),
    }));
  });
};

export const fetchQueueTimeseriesByIntegration = async (
  interval: MetricsInterval
): Promise<QueueTimeseriesResponse> => {
  const { data } = await monitoringAPI.get<QueueTimeseriesResponse>(
    `/queues/timeseries/by-integration${serializeQueryParameters(
      getScoringTimeseriesRange(interval)
    )}`
  );

  return data;
};

export const queueTimeseriesToCharts = (
  response: QueueTimeseriesResponse,
  interval: MetricsInterval
): MetricsChartData[] => {
  const timestamps = response.timestamps.map(toChartTimestamp);
  const stepMs = response.stepSeconds * 1000;

  return response.integrations.flatMap((integration) => {
    const charts: MetricsChartData[] = [];

    if (integration.request) {
      charts.push(
        ...queueCharts(
          integration,
          'request',
          integration.request,
          timestamps,
          stepMs,
          interval
        )
      );
    }

    if (integration.response) {
      charts.push(
        ...queueCharts(
          integration,
          'response',
          integration.response,
          timestamps,
          stepMs,
          interval
        )
      );
    }

    return charts;
  });
};
